import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Trophy, Users } from "lucide-react";
import { apiClient } from "../App";

const ScoreRecordingModal = ({ open, onOpenChange, session, attendees, onSaved }) => {
  const [matches, setMatches] = useState([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && session) {
      // Load existing matches
      loadExistingMatches();
    }
  }, [open, session]);

  const loadExistingMatches = async () => {
    try {
      const res = await apiClient.get(`/sessions/${session.id}/matches`);
      if (res.data && res.data.length > 0) {
        setMatches(res.data.map(m => ({
          id: m.id,
          matchType: m.match_type,
          teamA: m.team_a,
          teamB: m.team_b,
          scoreA: m.score_a.toString(),
          scoreB: m.score_b.toString()
        })));
      } else {
        // Add default empty match
        addNewMatch();
      }
    } catch (error) {
      addNewMatch();
    }
  };

  const addNewMatch = () => {
    setMatches(prev => [...prev, {
      id: `new-${Date.now()}`,
      matchType: "doubles",
      teamA: [],
      teamB: [],
      scoreA: "",
      scoreB: ""
    }]);
  };

  const removeMatch = (index) => {
    setMatches(prev => prev.filter((_, i) => i !== index));
  };

  const updateMatch = (index, field, value) => {
    setMatches(prev => prev.map((m, i) => {
      if (i === index) {
        return { ...m, [field]: value };
      }
      return m;
    }));
  };

  const validateMatches = () => {
    for (let i = 0; i < matches.length; i++) {
      const m = matches[i];
      const requiredPlayers = m.matchType === "doubles" ? 2 : 1;
      
      if (m.teamA.length !== requiredPlayers) {
        toast.error(`Match ${i + 1}: Team A needs ${requiredPlayers} player(s)`);
        return false;
      }
      if (m.teamB.length !== requiredPlayers) {
        toast.error(`Match ${i + 1}: Team B needs ${requiredPlayers} player(s)`);
        return false;
      }
      if (!m.scoreA || isNaN(parseInt(m.scoreA))) {
        toast.error(`Match ${i + 1}: Enter valid score for Team A`);
        return false;
      }
      if (!m.scoreB || isNaN(parseInt(m.scoreB))) {
        toast.error(`Match ${i + 1}: Enter valid score for Team B`);
        return false;
      }
      if (parseInt(m.scoreA) === parseInt(m.scoreB)) {
        toast.error(`Match ${i + 1}: Scores cannot be tied`);
        return false;
      }
      // Check for duplicate players in same match
      const allPlayers = [...m.teamA, ...m.teamB];
      if (new Set(allPlayers).size !== allPlayers.length) {
        toast.error(`Match ${i + 1}: Same player cannot be in both teams`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (matches.length === 0) {
      toast.error("Add at least one match");
      return;
    }

    if (!validateMatches()) return;

    setSaving(true);
    try {
      const payload = {
        session_id: session.id,
        matches: matches.map(m => ({
          team_a: m.teamA,
          team_b: m.teamB,
          score_a: parseInt(m.scoreA),
          score_b: parseInt(m.scoreB),
          match_type: m.matchType
        })),
        send_summary_email: sendEmail
      };

      await apiClient.post(`/sessions/${session.id}/matches`, payload);
      toast.success(`Saved ${matches.length} match(es)${sendEmail ? ' and sent summary emails' : ''}`);
      onSaved && onSaved();
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to save matches");
    } finally {
      setSaving(false);
    }
  };

  const getWinner = (match) => {
    const scoreA = parseInt(match.scoreA) || 0;
    const scoreB = parseInt(match.scoreB) || 0;
    if (scoreA > scoreB) return "A";
    if (scoreB > scoreA) return "B";
    return null;
  };

  const getPlayerName = (userId) => {
    const player = attendees.find(a => a.user_id === userId);
    return player ? player.user_name : "Unknown";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#064E3B]" />
            Record Match Scores
          </DialogTitle>
          <p className="text-sm text-slate-500">
            Session: {session?.turf_location_name} • {session?.date}
          </p>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {matches.map((match, index) => (
            <div key={match.id} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-slate-700">Match {index + 1}</span>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500">Singles</Label>
                    <Switch
                      checked={match.matchType === "doubles"}
                      onCheckedChange={(checked) => {
                        updateMatch(index, "matchType", checked ? "doubles" : "singles");
                        updateMatch(index, "teamA", []);
                        updateMatch(index, "teamB", []);
                      }}
                    />
                    <Label className="text-xs text-slate-500">Doubles</Label>
                  </div>
                  {matches.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeMatch(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-4 items-center">
                {/* Team A */}
                <div className={`p-3 rounded-lg border-2 ${getWinner(match) === "A" ? "border-green-500 bg-green-50" : "border-slate-200 bg-white"}`}>
                  <Label className="text-xs text-slate-500 mb-2 block">
                    Team A {getWinner(match) === "A" && <Trophy className="w-3 h-3 inline text-green-600" />}
                  </Label>
                  <div className="space-y-2">
                    <Select
                      value={match.teamA[0] || ""}
                      onValueChange={(value) => {
                        const newTeam = [...match.teamA];
                        newTeam[0] = value;
                        updateMatch(index, "teamA", newTeam);
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select player 1" />
                      </SelectTrigger>
                      <SelectContent>
                        {attendees.map((a) => (
                          <SelectItem key={a.user_id} value={a.user_id} disabled={match.teamB.includes(a.user_id) || (match.teamA[1] === a.user_id)}>
                            {a.user_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {match.matchType === "doubles" && (
                      <Select
                        value={match.teamA[1] || ""}
                        onValueChange={(value) => {
                          const newTeam = [...match.teamA];
                          newTeam[1] = value;
                          updateMatch(index, "teamA", newTeam);
                        }}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select player 2" />
                        </SelectTrigger>
                        <SelectContent>
                          {attendees.map((a) => (
                            <SelectItem key={a.user_id} value={a.user_id} disabled={match.teamB.includes(a.user_id) || (match.teamA[0] === a.user_id)}>
                              {a.user_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                {/* Score */}
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    value={match.scoreA}
                    onChange={(e) => updateMatch(index, "scoreA", e.target.value)}
                    className="w-16 h-12 text-center text-xl font-bold"
                    placeholder="0"
                  />
                  <span className="text-slate-400 font-bold">vs</span>
                  <Input
                    type="number"
                    min="0"
                    value={match.scoreB}
                    onChange={(e) => updateMatch(index, "scoreB", e.target.value)}
                    className="w-16 h-12 text-center text-xl font-bold"
                    placeholder="0"
                  />
                </div>

                {/* Team B */}
                <div className={`p-3 rounded-lg border-2 ${getWinner(match) === "B" ? "border-green-500 bg-green-50" : "border-slate-200 bg-white"}`}>
                  <Label className="text-xs text-slate-500 mb-2 block">
                    Team B {getWinner(match) === "B" && <Trophy className="w-3 h-3 inline text-green-600" />}
                  </Label>
                  <div className="space-y-2">
                    <Select
                      value={match.teamB[0] || ""}
                      onValueChange={(value) => {
                        const newTeam = [...match.teamB];
                        newTeam[0] = value;
                        updateMatch(index, "teamB", newTeam);
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select player 1" />
                      </SelectTrigger>
                      <SelectContent>
                        {attendees.map((a) => (
                          <SelectItem key={a.user_id} value={a.user_id} disabled={match.teamA.includes(a.user_id) || (match.teamB[1] === a.user_id)}>
                            {a.user_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {match.matchType === "doubles" && (
                      <Select
                        value={match.teamB[1] || ""}
                        onValueChange={(value) => {
                          const newTeam = [...match.teamB];
                          newTeam[1] = value;
                          updateMatch(index, "teamB", newTeam);
                        }}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select player 2" />
                        </SelectTrigger>
                        <SelectContent>
                          {attendees.map((a) => (
                            <SelectItem key={a.user_id} value={a.user_id} disabled={match.teamA.includes(a.user_id) || (match.teamB[0] === a.user_id)}>
                              {a.user_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Add Match Button */}
          <Button
            variant="outline"
            onClick={addNewMatch}
            className="w-full border-dashed border-2 border-slate-300 hover:border-[#064E3B] hover:bg-[#064E3B]/5"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Another Match
          </Button>

          {/* Email Option */}
          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <Checkbox
              id="sendEmail"
              checked={sendEmail}
              onCheckedChange={setSendEmail}
            />
            <Label htmlFor="sendEmail" className="text-sm text-blue-800 cursor-pointer">
              Send match summary email to all session attendees
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || matches.length === 0}
            className="bg-[#064E3B] hover:bg-[#065f46]"
          >
            {saving ? "Saving..." : `Save ${matches.length} Match(es)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScoreRecordingModal;
